import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  makeStyles,
} from '@fluentui/react-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FolderTree } from 'lucide-react';

import { ApiError } from '../api/client';
import { assignMigrationGroup } from '../api/migrationGroups';
import GroupLabel from './GroupLabel';
import GroupPicker from './GroupPicker';

const useStyles = makeStyles({
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: '6px',
    minWidth: 'auto',
    paddingLeft: '4px',
    paddingRight: '4px',
    fontWeight: '400',
  },
  hint: {
    fontSize: '13px',
    color: '#71717a',
    marginBottom: '12px',
  },
});

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const body = error.body as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return error instanceof Error ? error.message : 'Unexpected error';
}

export interface MigrationGroupFacetProps {
  migrationId: string;
  migrationGroupId: string | null;
  migrationGroupName: string | null;
  /**
   * FD-491 — optional CONTROLLED open state, so a second trigger can drive the
   * same dialog.
   *
   * The campaign header now carries a `···` menu whose "Assign to group" item
   * opens this dialog, alongside the facet button itself. Both are entry points
   * to ONE dialog instance owned by `MigrationLayout`; the alternative — a
   * second dialog rendered by the menu — would be two mutation surfaces against
   * one row that can disagree about what was saved.
   *
   * Omit both props and the component stays exactly as uncontrolled as it was:
   * every existing call site and test is untouched.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * FD-502 — render the DIALOG ONLY, with no button of its own.
   *
   * The campaign header's fact row carried this facet's trigger and is gone;
   * the `···` menu's "Assign to group" is now the only entry point, and it
   * needs a dialog to open. Mounting this triggerless keeps that menu item a
   * real action (FD-ADR-043 D4 — no affordance with nothing behind it) without
   * a stray group button floating in a header that no longer has a row for it,
   * and without a SECOND dialog instance beside the menu.
   *
   * Defaults to true, so every other call site is untouched.
   */
  showTrigger?: boolean;
}

// The PRIMARY assignment surface: a facet in the migration header, beside
// status / wave / type, opening a small dialog. Assignment belongs on the thing
// being assigned — an operator who has just opened a migration and realised it
// is filed wrong should not have to go find a group page.
export default function MigrationGroupFacet({
  migrationId,
  migrationGroupId,
  migrationGroupName,
  open: controlledOpen,
  onOpenChange,
  showTrigger = true,
}: MigrationGroupFacetProps) {
  const styles = useStyles();
  const queryClient = useQueryClient();

  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = (next: boolean) => {
    // The internal state is left alone while controlled, so the two can never
    // race and the parent stays the single source of truth.
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  const [pending, setPending] = useState<string | null>(migrationGroupId);

  // Re-seed whenever the dialog opens, so cancelling and reopening does not
  // present a stale pending choice as though it were the current one.
  useEffect(() => {
    if (open) setPending(migrationGroupId);
  }, [open, migrationGroupId]);

  const mutation = useMutation({
    mutationFn: () => assignMigrationGroup(migrationId, pending),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['migration', migrationId] });
      queryClient.invalidateQueries({ queryKey: ['migrations'] });
      // The group's rollups moved on BOTH sides of a move — the one it left and
      // the one it joined — so invalidate the whole group surface rather than
      // trying to name them.
      queryClient.invalidateQueries({ queryKey: ['migration-groups'] });
      queryClient.invalidateQueries({ queryKey: ['migration-group'] });
      setOpen(false);
    },
  });

  return (
    <>
      {showTrigger && (
        <Button
          appearance="subtle"
          size="small"
          className={styles.trigger}
          icon={<FolderTree size={14} strokeWidth={1.5} />}
          onClick={() => setOpen(true)}
        >
          <GroupLabel
            migrationGroupId={migrationGroupId}
            migrationGroupName={migrationGroupName}
          />
        </Button>
      )}

      <Dialog open={open} onOpenChange={(_, d) => setOpen(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Migration group</DialogTitle>
            <DialogContent>
              <p className={styles.hint}>
                Groups organize migrations for reporting. Changing a group has no effect on how
                this migration runs.
              </p>
              <GroupPicker value={pending} onChange={setPending} />
              {mutation.isError && (
                <div style={{ marginTop: '12px' }}>
                  <MessageBar intent="error">
                    <MessageBarBody>{errorMessage(mutation.error)}</MessageBarBody>
                  </MessageBar>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate()}
              >
                {mutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}
